import { buildFscSeatTypes, isFscOpsSeat, partitionFscSeatTypes } from 'pricing-pages-ui-components/fsc-configure/fscCatalogHelpers';
import { buildFscCardStatus } from 'pricing-pages-ui-components/fsc-configure/fscCardStatus';
import { buildFscSeatsBannerConfig, getFscSeatsBannerTrackingName } from 'pricing-pages-ui-components/fsc-configure/fscSeatsBanner';
import { useHistory, useLocation } from 'quartz-routing/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Screens } from '../../types/PublicNavItem';
import { TRACKING_SOURCES, trackAdditionalUnitsPicker, trackBannerInteraction, trackButtonClick } from '../../utils/tracking';
import { withFscProductQuantity } from './fscUrlCart';
export function useFscSeatsCard({
  catalog,
  selectedTier,
  cart,
  currencyCode,
  pricesByApiName,
  isPriceLoading,
  onSelectTier
}) {
  const history = useHistory();
  const {
    search
  } = useLocation();
  const [isOtherExpanded, setIsOtherExpanded] = useState(false);
  const [isOpsBannerVisible, setIsOpsBannerVisible] = useState(false);
  const allSeatTypes = useMemo(() => {
    const [primarySeat, ...otherSeats] = cart.seats;
    if (!primarySeat) return [];
    return buildFscSeatTypes(catalog, selectedTier, pricesByApiName, {
      quantity: primarySeat.quantity,
      min: 1
    }, Object.fromEntries(otherSeats.map(seat => [seat.productApiName, seat.quantity])));
  }, [cart, catalog, pricesByApiName, selectedTier]);
  const onSeatTypeQuantityChange = useCallback((seatTypeId, quantity) => {
    var _allSeatTypes$find;
    const previousValue = (_allSeatTypes$find = allSeatTypes.find(seat => seat.seatTypeId === seatTypeId)) === null || _allSeatTypes$find === void 0 ? void 0 : _allSeatTypes$find.stepper.quantity;
    if (previousValue === undefined || previousValue === quantity) return;
    trackAdditionalUnitsPicker({
      action: quantity > previousValue ? 'stepper increase' : 'stepper decrease',
      displayedUnitQuantity: quantity,
      previousValue,
      apiName: seatTypeId,
      productNames: [seatTypeId],
      productLevel: selectedTier,
      activeCurrencyCode: currencyCode,
      trackingSource: TRACKING_SOURCES.FSC_CONFIGURE
    });
    history.replace({
      search: withFscProductQuantity(search, seatTypeId, quantity)
    });
  }, [allSeatTypes, currencyCode, history, search, selectedTier]);
  const onAddSeatType = useCallback(seatTypeId => {
    trackButtonClick({
      action: 'add',
      name: `add-${seatTypeId}`,
      trackingSource: TRACKING_SOURCES.FSC_CONFIGURE,
      screen: Screens.FSC_CONFIGURE
    });
    history.replace({
      search: withFscProductQuantity(search, seatTypeId, 1)
    });
    if (isFscOpsSeat(catalog, seatTypeId)) {
      setIsOpsBannerVisible(true);
    }
  }, [catalog, history, search]);
  const seatsCardProps = useMemo(() => {
    if (allSeatTypes.length === 0) return null;
    return Object.assign({
      currencyCode
    }, partitionFscSeatTypes(allSeatTypes), {
      isOtherSeatOptionsExpanded: isOtherExpanded,
      onToggleOtherSeatOptions: setIsOtherExpanded,
      onSeatTypeQuantityChange,
      onAddSeatType
    });
  }, [allSeatTypes, currencyCode, isOtherExpanded, onAddSeatType, onSeatTypeQuantityChange]);
  const bannerConfig = useMemo(() => seatsCardProps ? buildFscSeatsBannerConfig({
    catalog,
    tier: selectedTier,
    seatTypes: seatsCardProps.seatTypes,
    isOpsBannerVisible
  }) : {
    type: 'none'
  }, [catalog, isOpsBannerVisible, seatsCardProps, selectedTier]);
  const bannerTrackingName = getFscSeatsBannerTrackingName(bannerConfig);
  useEffect(() => {
    if (!bannerTrackingName) return;
    trackBannerInteraction({
      action: 'view',
      name: bannerTrackingName,
      screen: Screens.FSC_CONFIGURE
    });
  }, [bannerTrackingName]);
  const onUpgradeFromBanner = useCallback(() => {
    if (bannerConfig.type !== 'seatLimitReached' || !bannerConfig.upgradeTier) {
      return;
    }
    trackBannerInteraction({
      action: 'click',
      name: `${bannerConfig.seatApiName}-seat-limit`,
      screen: Screens.FSC_CONFIGURE
    });
    onSelectTier(bannerConfig.upgradeTier);
  }, [bannerConfig, onSelectTier]);
  const onDismissOpsBanner = useCallback(() => {
    trackBannerInteraction({
      action: 'dismiss',
      name: 'ops-seat-dependency',
      screen: Screens.FSC_CONFIGURE
    });
    setIsOpsBannerVisible(false);
  }, []);
  return {
    status: buildFscCardStatus({
      data: seatsCardProps,
      isPriceLoading
    }),
    bannerConfig,
    onUpgradeFromBanner,
    onDismissOpsBanner
  };
}